import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import {
  CheckoutFormProps,
  DefaultFormActions,
  Form,
  useEnhancedForm,
} from '../../apiForm/form';
import { NumberField, SelectField, TextField } from '../../apiForm/form/input';
import useResourceTranslator from '../../apiForm/resource/useResourceTranslator';
import CheckboxField from '../../apiForm/form/input/CheckboxField';
import ExpandableFieldset from '../../apiForm/form/ExpandableFieldset';

const DEFAULT_VALUES: any = {
  priceSelectionStrategy: 'standard',
};

export default function GeneralForm({
  defaultValues = DEFAULT_VALUES,
  errors = {},
  onSubmit,
  readonly,
}: CheckoutFormProps<any>) {
  const t = useResourceTranslator();

  const { handleSubmit, control, formState, getValues, reset } =
    useEnhancedForm({ defaultValues, errors });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ExpandableFieldset category="general">
        <TextField
          control={control}
          name="id"
          disabled
          label={t('checkoutConfig.label.general.id')}
        />

        <SelectField
          control={control}
          name="weightRoundingMode"
          disabled={readonly}
          label={t('checkoutConfig.label.general.weightRoundingMode')}
          required
          rules={{ required: true }}
        >
          <MenuItem value="up">Up</MenuItem>
          <MenuItem value="down">Down</MenuItem>
          <MenuItem value="commercial">Commercial</MenuItem>
        </SelectField>

        <CheckboxField
          control={control}
          name="trustClientPrice"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.trustClientPrice')} (${t('checkoutConfig.helperText.general.trustClientPrice')})`}
        />

        <CheckboxField
          control={control}
          name="trustFallbackPrice"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.trustFallbackPrice')} (${t('checkoutConfig.helperText.general.trustFallbackPrice')})`}
        />

        <SelectField
          control={control}
          name="priceSelectionStrategy"
          disabled={readonly}
          label={t('checkoutConfig.label.general.priceSelectionStrategy')}
          helperText={t(
            'checkoutConfig.helperText.general.priceSelectionStrategy',
          )}
          required
          rules={{ required: true }}
        >
          <MenuItem value="standard">Standard</MenuItem>
          <MenuItem value="bestPrice">Best Price</MenuItem>
          <MenuItem value="strauss">Strauss</MenuItem>
        </SelectField>

        <TextField
          control={control}
          name="currency"
          disabled={readonly}
          label={t('checkoutConfig.label.general.currency')}
          helperText={t('checkoutConfig.helperText.general.currency')}
          required
          rules={{ required: true }}
        />

        <TextField
          control={control}
          name="timezone"
          disabled={readonly}
          label={t('checkoutConfig.label.general.timezone')}
          helperText={t('checkoutConfig.helperText.general.timezone')}
          rules={{ required: true }}
          required
        />

        <TextField
          control={control}
          name="locale"
          disabled={readonly}
          label={t('checkoutConfig.label.general.locale')}
          helperText={t('checkoutConfig.helperText.general.locale')}
        />

        <SelectField
          control={control}
          name="totalRoundingMode"
          disabled={readonly}
          label={t('checkoutConfig.label.general.totalRoundingMode')}
          helperText={t('checkoutConfig.helperText.general.totalRoundingMode')}
        >
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="rappenRounding">Rappen Rounding</MenuItem>
        </SelectField>

        <CheckboxField
          control={control}
          name="throttleProcessCreation"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.throttleProcessCreation')} (${t('checkoutConfig.helperText.general.throttleProcessCreation')})`}
        />

        <CheckboxField
          control={control}
          name="useDeviceSpecificSequenceNumbers"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.useDeviceSpecificSequenceNumbers')} (${t('checkoutConfig.helperText.general.useDeviceSpecificSequenceNumbers')})`}
        />

        <CheckboxField
          control={control}
          name="generateReceiptForAllCheckouts"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.generateReceiptForAllCheckouts')} (${t('checkoutConfig.helperText.general.generateReceiptForAllCheckouts')})`}
        />

        <SelectField
          control={control}
          name="receiptNumberResetPeriod"
          disabled={readonly}
          label={t('checkoutConfig.label.general.receiptNumberResetPeriod')}
        >
          <MenuItem value="daily">Daily</MenuItem>
        </SelectField>

        <NumberField
          control={control}
          name="receiptNumberOffset"
          disabled={readonly}
          label={t('checkoutConfig.label.general.receiptNumberOffset')}
        />

        <NumberField
          control={control}
          name="maximumManualDiscountInPercent"
          disabled={readonly}
          label={t(
            'checkoutConfig.label.general.maximumManualDiscountInPercent',
          )}
        />

        <CheckboxField
          control={control}
          name="enableCustomerSearch"
          disabled={readonly}
          label={t('checkoutConfig.label.general.enableCustomerSearch')}
        />

        <TextField
          control={control}
          name="customerSearchURL"
          disabled={readonly}
          label={t('checkoutConfig.label.general.customerSearchUrl')}
          helperText={t('checkoutConfig.helperText.general.customerSearchUrl')}
        />
      </ExpandableFieldset>

      <ExpandableFieldset category="posPriceManagementSettings">
        <CheckboxField
          control={control}
          name="posSetPriceIfUnknown"
          disabled={readonly}
          label={t('checkoutConfig.label.general.posSetPriceIfUnknown')}
        />

        <CheckboxField
          control={control}
          name="posEnterPrice"
          disabled={readonly}
          label={t('checkoutConfig.label.general.posEnterPrice')}
        />

        <SelectField
          control={control}
          name="priceOverwriteStrategy"
          disabled={readonly}
          label={t('checkoutConfig.label.general.priceOverwriteStrategy')}
          helperText={t(
            'checkoutConfig.helperText.general.priceOverwriteStrategy',
          )}
        >
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="replace">Replace</MenuItem>
          <MenuItem value="reduceOnly">Reduce Only</MenuItem>
        </SelectField>
      </ExpandableFieldset>

      <ExpandableFieldset category="loyaltyProgram">
        <CheckboxField
          control={control}
          name="allowLoyaltyCards"
          disabled={readonly}
          label={t('checkoutConfig.label.general.allowLoyaltyCards')}
        />

        <SelectField
          control={control}
          name="loyaltyProgram"
          disabled={readonly}
          label={t('checkoutConfig.label.loyaltyProgram.loyaltyProgram')}
          helperText={t(
            'checkoutConfig.helperText.loyaltyProgram.loyaltyProgram',
          )}
        >
          <MenuItem value="">None</MenuItem>
          <MenuItem value="payback">Payback</MenuItem>
        </SelectField>
      </ExpandableFieldset>

      <ExpandableFieldset category="messages">
        <TextField
          control={control}
          name="messages.manualDiscountCart"
          disabled={readonly}
          label={t('checkoutConfig.label.messages.manualDiscountCart')}
          helperText={t(
            'checkoutConfig.helperText.messages.manualDiscountCart',
          )}
        />
        <TextField
          control={control}
          name="messages.manualDiscountLineItem"
          disabled={readonly}
          label={t('checkoutConfig.label.messages.manualDiscountLineItem')}
          helperText={t(
            'checkoutConfig.helperText.messages.manualDiscountLineItem',
          )}
        />
        <TextField
          control={control}
          name="messages.balanceDiscount"
          disabled={readonly}
          label={t('checkoutConfig.label.messages.balanceDiscount')}
          helperText={t('checkoutConfig.helperText.messages.balanceDiscount')}
        />
      </ExpandableFieldset>

      <ExpandableFieldset category="voiding">
        <CheckboxField
          control={control}
          name="allowVoiding"
          disabled={readonly}
          label={`${t('checkoutConfig.label.general.allowVoiding')} (${t('checkoutConfig.helperText.general.allowVoiding')})`}
        />
      </ExpandableFieldset>

      <ExpandableFieldset category="features">
        <TextField
          control={control}
          name="features"
          disabled={readonly}
          label={t('checkoutConfig.label.general.features')}
          helperText={t('checkoutConfig.helperText.general.features')}
        />
        <a
          href="https://github.com/snabble/checkout/blob/master/core/config/feature/flags.go"
          target="_blank"
          rel="noreferrer"
        >
          {t('links.checkoutFeatures')}
        </a>
      </ExpandableFieldset>

      <DefaultFormActions
        formState={formState}
        cancelNavigationTarget="/"
        noCancelNavigation
        reset={reset}
        getValues={getValues}
      />
    </Form>
  );
}
